import { useState } from "react";
import {
  CircleCheckIcon,
  FlameIcon,
  GaugeIcon,
  MountainIcon,
} from "lucide-react";

import { type PortfolioMatrixJob } from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { useCreateJobUpdate } from "@/hooks/mutations/useCreateJobUpdate";
import { ChipSelectOption, JobDifficulty } from "@/types";

import { DropDownCell } from "@/components";

type Difficulty = NonNullable<PortfolioMatrixJob["difficulty"]>;

const DIFFICULTY_TO_ENUM: Record<Difficulty, JobDifficulty> = {
  easy: JobDifficulty.EASY,
  moderate: JobDifficulty.MODERATE,
  difficult: JobDifficulty.DIFFICULT,
  "very-difficult": JobDifficulty.VERY_DIFFICULT,
};

const DIFFICULTY_OPTIONS: ChipSelectOption<Difficulty>[] = [
  {
    value: "easy",
    label: "Easy",
    icon: CircleCheckIcon,
    selectedClassName:
      "border-[#27AE60] bg-[#27AE60]/15 text-[#1D7A42] hover:bg-[#27AE60]/20",
    iconClassName: "text-[#1D7A42]",
  },
  {
    value: "moderate",
    label: "Moderate",
    icon: GaugeIcon,
    selectedClassName:
      "border-[#F2C94C] bg-[#F2C94C]/20 text-[#8A7220] hover:bg-[#F2C94C]/30",
    iconClassName: "text-[#8A7220]",
  },
  {
    value: "difficult",
    label: "Difficult",
    icon: MountainIcon,
    selectedClassName:
      "border-[#E67E22] bg-[#E67E22]/15 text-[#c2410c] hover:bg-[#E67E22]/20",
    iconClassName: "text-[#c2410c]",
  },
  {
    value: "very-difficult",
    label: "Very difficult",
    icon: FlameIcon,
    selectedClassName:
      "border-[#D14343] bg-[#D14343]/15 text-[#9B3030] hover:bg-[#D14343]/20",
    iconClassName: "text-[#9B3030]",
  },
];

export const DifficultyCell = ({ job }: { job: PortfolioMatrixJob }) => {
  // Optimistic local value; the matrix cache is patched on success.
  const [difficulty, setDifficulty] = useState(job.difficulty);
  const { mutate: createJobUpdate } = useCreateJobUpdate();

  return (
    <DropDownCell
      options={DIFFICULTY_OPTIONS}
      placeholder="Set difficulty"
      value={difficulty}
      iconOnly
      onChange={(next) => {
        setDifficulty(next);
        createJobUpdate({
          companyId: job.companyId,
          jobId: job.id,
          createJobUpdateRequest: {
            difficulty: next ? DIFFICULTY_TO_ENUM[next] : null,
          },
        });
      }}
    />
  );
};
